import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SEOWrapper } from './SEOWrapper';
import { extractIdFromSlug, generateProfileSchema, generateProfileSlug } from '../utils/seo';
import { API_URL } from '../lib/api-client';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import ViewProfilePageNew from './ViewProfilePageNew';
import { formatMaritalStatus, formatIncome, extractString } from '../utils/profileFormatters';
import { logger } from '../utils/logger';
import { useAppSelector } from '../hooks/redux';

interface ProfileViewProps {
  onClose?: () => void;
}

export default function ProfileView({ onClose }: ProfileViewProps) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showAppBanner, setShowAppBanner] = useState(false);
  const { settings } = useAppSelector((state) => state.settings);

  useEffect(() => {
    // Show banner on mobile devices
    const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isMobile) {
      setShowAppBanner(true);
    }
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      // Reset state when slug changes
      setLoading(true);
      setProfile(null);

      if (!slug) {
        logger.error('❌ [ProfileView] No slug provided');
        toast.error('Invalid profile URL');
        navigate('/app');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        
        // Extract ID from slug
        const profileId = extractIdFromSlug(slug);

        logger.log('🔍 [ProfileView] Loading profile:', {
          slug,
          extractedId: profileId,
          hasToken: !!token
        });

        if (!profileId) {
          toast.error('Invalid profile URL');
          navigate('/app');
          return;
        }

        const headers: any = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        logger.log('📡 [ProfileView] Fetching from API:', `${API_URL}/profiles/${profileId}`);

        // Try multiple approaches to fetch the profile
        let data;
        let fetchError = null;

        // Approach 1: Try direct profile ID lookup
        try {
          const response = await axios.get(`${API_URL}/profiles/${profileId}`, { headers });
          data = response.data;

          logger.log('✅ [ProfileView] Successfully fetched via /profiles/:id');
        } catch (error: any) {
          fetchError = error;

          // Approach 2: If 404, try user-based lookup
          if (error.response?.status === 404) {
            logger.log('⚠️ [ProfileView] Profile not found, trying user-based lookup...');

            try {
              const userResponse = await axios.get(`${API_URL}/profiles/user/${profileId}`, { headers });
              data = userResponse.data;

              logger.log('✅ [ProfileView] Successfully fetched via /profiles/user/:id');
            } catch (userError: any) {
              // Approach 3: Try fetching all profiles and find by ID
              if (userError.response?.status === 404) {
                logger.log('⚠️ [ProfileView] User-based lookup failed, searching all profiles...');

                try {
                  const allProfilesResponse = await axios.get(`${API_URL}/profiles?limit=1000`, { headers });

                  const profiles = allProfilesResponse.data?.data?.profiles || [];
                  const foundProfile = profiles.find((p: any) =>
                    p._id === profileId ||
                    p.userId === profileId ||
                    p.userId?._id === profileId ||
                    p.id === profileId
                  );

                  if (foundProfile) {
                    data = { success: true, data: foundProfile };
                    logger.log('✅ [ProfileView] Found profile in all profiles list:', foundProfile);
                  } else {
                    throw new Error('Profile not found in any approach');
                  }
                } catch (searchError) {
                  // All approaches failed, throw original error
                  throw fetchError;
                }
              } else {
                throw userError;
              }
            }
          } else {
            throw error;
          }
        }

        if (data.success && data.data) {
          // Format the profile data to match ViewProfilePageNew expectations
          const profileData = data.data;

          // Safe photo processing
          const processPhotos = (photos: any) => {
            if (!photos || !Array.isArray(photos)) return [];

            return photos.map((photo: any) => {
              // Handle null/undefined
              if (!photo) return null;

              // If it's already a string URL
              if (typeof photo === 'string') {
                return photo.startsWith('http') ? photo : `${API_URL.replace('/api', '')}/uploads/${photo}`;
              }

              // If it's an object, keep it as an object but ensure URL is complete
              if (typeof photo === 'object') {
                const photoPath = photo.path || photo.url || photo.filename || '';
                if (photoPath) {
                  const fullUrl = photoPath.startsWith('http') ? photoPath : `${API_URL.replace('/api', '')}/uploads/${photoPath}`;
                  return { ...photo, url: fullUrl };
                }
              }

              return null;
            }).filter((photo: any) => photo !== null);
          };

          const processedPhotos = processPhotos(profileData.photos);

          // If photos array is empty but profilePhoto exists, use profilePhoto
          if (processedPhotos.length === 0 && profileData.profilePhoto) {
            const profilePhotoUrl = profileData.profilePhoto.startsWith('http')
              ? profileData.profilePhoto
              : `${API_URL.replace('/api', '')}/uploads/${profileData.profilePhoto}`;
            processedPhotos.push(profilePhotoUrl);
          }

          const formattedProfile = {
            id: profileData._id,
            _id: profileData._id,
            name: `${profileData.userId?.firstName || ''} ${profileData.userId?.lastName || ''}`.trim() || 'User',
            age: calculateAge(profileData.dateOfBirth),
            height: profileData.height ? `${profileData.height} cm` : "165 cm",
            location: `${profileData.city || ''}, ${profileData.state || ''}`.trim(),
            education: extractString(profileData.education || profileData.educationLevel),
            occupation: extractString(profileData.occupation),
            company: extractString(profileData.company),
            income: formatIncome(profileData.annualIncome),
            religion: extractString(profileData.religion),
            community: extractString(profileData.community, ''),
            caste: extractString(profileData.caste, ''),
            image: processedPhotos[0] || profileData.profilePhoto || '', // Use profilePhoto as fallback
            isPro: profileData.isPremium || false,
            isOnline: profileData.isOnline || false,
            profileManaged: profileData.profileManagedBy || 'Self',
            lastActive: profileData.lastActiveAt || 'Recently',
            about: profileData.about || profileData.bio || '',
            photos: processedPhotos,
            rawProfile: profileData,
            dateOfBirth: profileData.dateOfBirth,
            motherTongue: extractString(profileData.motherTongue, ''),
            maritalStatus: formatMaritalStatus(profileData.maritalStatus),
            familyType: extractString(profileData.familyType, ''),
            fatherOccupation: extractString(profileData.fatherOccupation, ''),
            motherOccupation: extractString(profileData.motherOccupation, ''),
            siblings: profileData.siblings,
            diet: extractString(profileData.diet, ''),
            smoking: extractString(profileData.smoking, ''),
            drinking: extractString(profileData.drinking, ''),
          };
          logger.log('✅ [ProfileView] Profile formatted successfully:', {
            id: formattedProfile.id,
            name: formattedProfile.name,
            photoCount: processedPhotos.length
          });
          setProfile(formattedProfile);
        } else {
          logger.error('❌ [ProfileView] Profile not found in response:', {
            success: data.success,
            message: data.message,
            data: data.data
          });
          toast.error(data.message || 'Profile not found');
          navigate('/app');
        }
      } catch (error: any) {
        logger.error('💥 [ProfileView] Error loading profile:', {
          error: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url
        });

        const errorMessage = error.response?.data?.message || error.message || 'Failed to load profile';
        toast.error(errorMessage);

        // Don't navigate away immediately on error - show the error
        setTimeout(() => {
          navigate('/app');
        }, 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="size-12 animate-spin text-pink-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const displayName = profile.name || (profile.userId?.firstName ? `${profile.userId.firstName} ${profile.userId.lastName || ''}`.trim() : 'User Profile');
  const firstName = displayName.split(' ')[0];
  const locationParts = profile.location ? profile.location.split(',') : ['Unknown Location'];
  const city = locationParts[0];
  const age = profile.age || 'Unknown';
  const occupation = profile.occupation || profile.career?.occupation || '';
  const religion = profile.religion || profile.background?.religion || 'Any';

  const seoTitle = `${displayName} - ${age} years, ${city} | Atootbandan`;
  const seoDescription = `View ${displayName}'s profile on Atootbandan. ${age} years old from ${city}. ${occupation ? `Working as ${occupation}.` : ''} Looking for a life partner.`;
  const seoKeywords = `${displayName}, matrimony, ${city} bride, ${city} groom, ${religion} matrimony, ${occupation} matrimony`;

  const profilePhoto = profile.photos?.length > 0 ? profile.photos[0] : profile.image;

  const targetPlayStoreUrl = settings?.appBanner?.playStoreUrl || 'https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp';
  const androidFallbackUrl = encodeURIComponent(targetPlayStoreUrl);
  const appLink = /android/i.test(navigator.userAgent)
    ? `intent://atootbandan.com/profile/${slug}#Intent;scheme=https;package=com.atootbandanmatrimonialapp;S.browser_fallback_url=${androidFallbackUrl};end`
    : targetPlayStoreUrl;

  const breadcrumbs = [
    { name: 'Home', url: window.location.origin },
    { name: 'Profiles', url: `${window.location.origin}/app` },
    { name: profile.name, url: window.location.href }
  ];

  return (
    <SEOWrapper
      title={seoTitle}
      description={seoDescription}
      keywords={seoKeywords}
      image={profilePhoto || '/default-profile.jpg'}
      type="profile"
      schema={generateProfileSchema(profile.rawProfile)}
      breadcrumbs={breadcrumbs}
    >
      <ViewProfilePageNew
        profile={profile}
        onBack={() => navigate(-1)}
        onNavigateToPlans={() => navigate('/app/plans')}
        onOpenChat={(userId) => {
          // Navigate to messages tab with userId
          localStorage.setItem('openChatWithUser', userId);
          navigate('/app?tab=messages');
        }}
        onViewProfile={(similarProfile) => {
          // Navigate to the similar profile using the helper function
          const firstName = similarProfile.firstName || similarProfile.userId?.firstName || 'User';
          const lastName = similarProfile.lastName || similarProfile.userId?.lastName || '';
          const profileId = similarProfile._id || similarProfile.id;
          const nextSlug = generateProfileSlug(firstName, lastName, profileId);
          navigate(`/profile/${nextSlug}`);
        }}
        onViewAllSimilar={() => {
          // Navigate to view all similar profiles page
          navigate('/app?tab=discovery&filter=similar');
        }}
      />
      
      {showAppBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] flex items-center justify-between pb-[env(safe-area-inset-bottom,16px)]">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-pink-600 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-sm">
              AB
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 leading-tight">Atoot Bandan App</p>
              <p className="text-xs text-gray-500 font-medium mt-0.5">View profile directly in app</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <a 
              href={appLink}
              className="bg-pink-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-sm hover:bg-pink-700 active:scale-95 transition-all inline-block text-center"
            >
              Open App
            </a>
            <button 
              onClick={() => setShowAppBanner(false)}
              className="text-gray-400 p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close banner"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
        </div>
      )}
    </SEOWrapper>
  );
}

const calculateAge = (dateOfBirth: string) => {
  if (!dateOfBirth) return 25;
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};